import React from 'react';
import {
  Building2,
  Rocket,
  Users,
  Eye,
  LayoutTemplate,
  BarChart3,
  FileCheck,
  ArrowRight,
  Check,
  Zap,
  Crown,
} from 'lucide-react';

const CONTACT_EMAIL = 'davies@decklink.co';

const ForTeams = () => {
  const personas = [
    {
      icon: Rocket,
      title: 'Startup accelerators and pre‑accelerators',
      color: 'from-blue-500 to-blue-600',
      description: 'Run cohorts with one shared playbook and full visibility into who’s raising.',
    },
    {
      icon: Building2,
      title: 'Venture studios / company builders',
      description: 'Keep portfolio companies on the same deck, pipeline, and reporting standards.',
      color: 'from-green-500 to-teal-600',
    },
    {
      icon: Users,
      title: 'VC funds / angel groups that run programs or cohorts',
      description: 'Give every founder one place to manage decks and outreach—you get cleaner data.',
      color: 'from-purple-500 to-violet-600',
    },
  ];

  const outcomes = [
    {
      icon: Eye,
      title: 'Portfolio‑wide visibility',
      color: 'from-blue-500 to-blue-600',
      body: 'See which startups are actually running a process, who’s sending decks, and where conversations stall.',
    },
    {
      icon: LayoutTemplate,
      title: 'Standardized process',
      body: 'Every founder follows the same playbook and uses the same template, instead of ad‑hoc spreadsheets and random tools.',
      color: 'from-green-500 to-teal-600',
    },
    {
      icon: FileCheck,
      title: 'Better investor experience',
      body: 'Investors get consistent links, clean data rooms, and updates, not a mess of docs and attachments.',
      color: 'from-purple-500 to-violet-600',
    },
    {
      icon: BarChart3,
      title: 'Proof for your LPs / stakeholders',
      body: 'Easy reporting on how many intros, views, and raises your program actually drove.',
      color: 'from-amber-500 to-orange-600',
    },
  ];

  const features = [
    'Organization‑level workspace with multiple startups under one umbrella.',
    'Admin view for staff: monitor activity across all companies (deck sent, views, last touch).',
    'Shared templates: fundraising pipeline, investor lists, email copy that every founder can clone.',
    'Access control and permissions (admins vs founders, who can see what).',
    'Integrations and export (CSV or CRM sync) so programs can pull reporting into their stack.',
  ];

  const programTiers = [
    {
      name: 'Program plan',
      price: 'From $299',
      period: 'per cohort (3–6 months)',
      description: 'One price per cohort size',
      details: ['Up to 20 startups', 'Up to 50 startups', 'Custom cohort sizes'],
      note: 'Flat pricing for the program duration plus optional ongoing access for alumni at a discount.',
    },
    {
      name: 'Partner / Enterprise',
      price: 'Custom',
      period: 'white-glove setup',
      description: 'Higher-touch option',
      details: ['Onboarding & training', 'Dedicated CSM', 'Custom reporting'],
      note: 'For accelerators and funds that want dedicated support.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="text-center max-w-[88rem] mx-auto w-full relative">
          {/* Glow – match Landing */}
          <div className="absolute left-1/2 -translate-x-1/2 top-24 -z-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/20 rounded-full blur-3xl" />
          {/* Hero */}
          <div className="mb-16 flex flex-col items-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 sm:mb-6 px-4 text-white max-w-4xl">
              For accelerators, studios, and funds that help founders raise.
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-2 max-w-2xl leading-relaxed px-4">
              Give every company in your portfolio one place to manage decks, outreach, and investor updates, so you get cleaner data and better outcomes.
            </p>
            <p className="text-sm sm:text-base text-slate-500 mb-8 px-4">
              Program-friendly pricing. One workspace for your whole cohort.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl text-xl font-medium transition-all min-w-[280px]"
            >
              Get in touch
              <ArrowRight className="w-6 h-6" />
            </a>
          </div>

          {/* Who we serve */}
          <section className="mb-16">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-6 px-4 text-center w-full">Who we serve</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {personas.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-10 hover:border-blue-500/30 transition-all duration-300 group h-full min-h-[380px] flex flex-col"
                  >
                    <div className="text-center flex flex-col flex-1">
                      <div className={`bg-gradient-to-r ${item.color} rounded-2xl p-5 w-fit mx-auto mb-6 group-hover:scale-110 transition-transform shrink-0`}>
                        <Icon className="text-white" size={40} />
                      </div>
                      <h3 className="text-xl md:text-2xl font-semibold text-white mb-4 shrink-0">{item.title}</h3>
                      <p className="text-slate-300 leading-relaxed text-base md:text-lg flex-1 min-h-0">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Why teams use DeckLink */}
          <section className="mb-16">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-6 px-4 text-center w-full">Why teams use DeckLink</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {outcomes.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-10 hover:border-blue-500/30 transition-all duration-300 group h-full min-h-[380px] flex flex-col"
                  >
                    <div className="text-center flex flex-col flex-1">
                      <div className={`bg-gradient-to-r ${item.color} rounded-2xl p-5 w-fit mx-auto mb-6 group-hover:scale-110 transition-transform shrink-0`}>
                        <Icon className="text-white" size={40} />
                      </div>
                      <h3 className="text-xl md:text-2xl font-semibold text-white mb-4 shrink-0">{item.title}</h3>
                      <p className="text-slate-300 leading-relaxed text-base md:text-lg flex-1 min-h-0">{item.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* What you get */}
          <section className="mb-16 flex flex-col items-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-2 px-4 text-center w-full">Platform features for teams</h2>
            <p className="text-slate-400 mb-6 max-w-xl text-sm sm:text-base px-4 text-center">
              Everything you need to run a professional program—without per‑founder chaos.
            </p>
            <div className="w-full max-w-2xl mx-auto bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 sm:p-8 hover:border-blue-500/30 transition-all duration-300">
              <ul className="space-y-4">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300 text-base md:text-lg">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* B2B Pricing */}
          <section className="mb-16 flex flex-col items-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-2 px-4 text-center w-full">Program pricing</h2>
            <p className="text-slate-400 mb-6 max-w-xl text-sm sm:text-base px-4 text-center">
              Simple structure for cohorts—not per‑founder chaos.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 w-full max-w-3xl mx-auto">
              {programTiers.map((tier, i) => (
                <div
                  key={i}
                  className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-blue-500/30 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="rounded-2xl p-3 bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center shrink-0">
                      {i === 0 ? <Zap className="w-6 h-6 text-white" /> : <Crown className="w-6 h-6 text-white" />}
                    </div>
                    <h3 className="text-xl font-semibold text-white">{tier.name}</h3>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-bold text-white">{tier.price}</span>
                    <span className="text-slate-500 text-sm">{tier.period}</span>
                  </div>
                  <p className="text-slate-400 text-base mb-4">{tier.description}</p>
                  <ul className="space-y-2 mb-4">
                    {tier.details.map((d, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-300 text-sm sm:text-base">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-500 text-sm leading-relaxed">{tier.note}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Program%20pricing%20inquiry`}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-12 py-6 rounded-xl text-xl font-medium transition-all"
              >
                Get in touch
                <ArrowRight className="w-6 h-6" />
              </a>
            </div>
          </section>

          {/* Bottom: actual price + Founder pricing link – match Landing theme */}
          {/* <div className="pt-8 border-t border-slate-700/50 flex flex-col items-center text-center">
            <p className="text-slate-500 text-sm font-light px-4 mb-4 max-w-xl">
              <strong className="text-slate-400">Program plan from $299/cohort.</strong> Flat for 3–6 months. Founder pricing on <Link to="/pricing" className="text-blue-400 hover:text-blue-300 underline">our pricing page</Link>.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-slate-600 hover:border-blue-400 text-slate-300 hover:text-blue-400 rounded-xl font-medium transition-all"
            >
              Founder pricing
            </Link>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default ForTeams;
